import React from 'react';
import {
    Page,
    SkeletonBodyText,
    Card,
    Banner,
    Link,
    Select,
    TextField,
    ChoiceList,
} from '@shopify/polaris';
import OperationPreviewComponent from '../../components/OperationPreviewComponent';
import TagViewComponent from '../../components/TagViewComponent';
import { Helmet } from 'react-helmet';
import { apiFetch, ResourceTypes, resourceTypeToDataPermissions } from '../../common/Helpers';
import { RequiredPermissionsWarning } from '../../common/RequiredPermissionsWarning';

const PrefixDelimitCharacterOptions = [
    { label: "';' - Semicolon", value: "Semicolon" },
    { label: "':' - Colon", value: "Colon" },
    { label: "'-' - Dash", value: "Dash" },
    { label: "'_' - Underscore", value: "Underscore" },
];

class ScenarioFixTagCapitalizationPage extends React.Component {

    state = {
        step: null,
        resourceType: "Product",
        capitalizationType: "TitleCase",
        tagPrefixBehavior: null,
        tagPrefixBehaviorValue: "",
        selectedTags: [],
        isLoading: true,
        preview: null,
    };

    render() {

        if (this.state.step == null) {
            return (
                <div>
                    <Helmet>
                        <title>Tag Genie - Scenario - Fix Tag Capitalization</title>
                    </Helmet>
                    <Page title="Fix Tag Capitalization">
                        <SkeletonBodyText lines="20" />
                    </Page>
                </div>)
        }

        let resourcePlural = "products";
        let resourceSingular = "product";

        if (this.state.resourceType === "Customer") {
            resourcePlural = "customers";
            resourceSingular = "customer";
        }

        if (this.state.resourceType === "Order") {
            resourcePlural = "orders";
            resourceSingular = "order";
        }

        let tags = this.getTags();

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Scenario - Fix Tag Capitalization</title>
                </Helmet>
                {this.state.step === "create" &&
                    <Page title="Fix Tag Capitalization">
                        <Card primaryFooterAction={{ content: 'Preview Changes', onAction: this.handlePreview, loading: this.state.isLoading }}>
                            <Card.Section>
                                <Banner>
                                    <p>If you require more advanced functionality, consider using a <Link url="/operations/new/custom">Custom Tag Operation</Link>.
                                    </p>
                                </Banner>
                            </Card.Section>
                            <Card.Section title="Resource type">
                                <Select options={ResourceTypes} value={this.state.resourceType} onChange={this.onResourceChange} />
                                <RequiredPermissionsWarning requiredPermissions={[resourceTypeToDataPermissions(this.state.resourceType)]} />
                            </Card.Section>
                            <Card.Section title="Select Tags To Fix">
                                <TagViewComponent
                                    showMarkUnmark={true}
                                    showHighlight={false}
                                    canSelect={true}
                                    tags={tags}
                                    selectedTags={this.state.selectedTags}
                                    onSelectedChange={this.handleSelectedTagsChange}
                                    tagActionText="Click on a tag to select it for being merged." />
                            </Card.Section>
                            <Card.Section title="Choosing Tag Capitalization Style">
                                <p>Choose which capitalization style to apply to the selected tags.</p>
                                <ChoiceList
                                    choices={[
                                        {
                                            label: 'Uppercase',
                                            value: "UpperCase",
                                            helpText: "All letters will be changed to uppercase."
                                        },
                                        {
                                            label: 'Lowercase',
                                            value: "LowerCase",
                                            helpText: "All letters will be changed to lowercase."
                                        },
                                        {
                                            label: 'Sentence case',
                                            value: "SentenceCase",
                                            helpText: "Capitalize only the first word of each tag."
                                        },
                                        {
                                            label: 'Title case',
                                            value: "TitleCase",
                                            helpText: "Capitalize every word of each tag."
                                        },
                                    ]}

                                    selected={[this.state.capitalizationType]}
                                    onChange={this.handleCapitalizationTypeChange}
                                />
                            </Card.Section>
                            <Card.Section title="Ignore Tag Prefix Settings">
                                <p>You can choose to ignore tag prefixes so that they are not affected by capitalization changes.</p>
                                <ChoiceList
                                    choices={[
                                        {
                                            label: 'Do Not Ignore',
                                            value: null,
                                            helpText: "Prefix will be affected by capitalization changes."
                                        },
                                        {
                                            label: 'Ignore Specific Prefix',
                                            value: 'specific',
                                            helpText: "The prefix you specify will not be affected by capitalization changes.",
                                            renderChildren: this.renderOptions
                                        },
                                        {
                                            label: 'Ignore Prefix By Delimiter',
                                            value: 'delimiter',
                                            helpText: "Any text before the delimiter will be considered as prefix and will not be affected by capitalization changes.",
                                            renderChildren: this.renderOptions
                                        },
                                    ]}

                                    selected={[this.state.tagPrefixBehavior]}
                                    onChange={this.handleTagPrefixBehaviorChange}
                                />
                            </Card.Section>
                        </Card>
                    </Page>
                }

                {this.state.step === "preview" &&
                    <Page title="Preview Changes">
                        <Card
                            title="Preview Changes"
                            primaryFooterAction={{ content: "Execute Changes", disabled: !this.state.preview.canExecute || this.state.isLoading, onAction: this.handleSubmit, loading: this.state.isLoading }}
                            secondaryFooterActions={[{ content: 'Back', disabled: this.state.isLoading, onAction: this.handleBack }]}>
                            <OperationPreviewComponent
                                preview={this.state.preview}
                                resourcePlural={resourcePlural}
                                resourceSingular={resourceSingular} />
                        </Card>
                    </Page>}
            </div>
        );
    }

    renderOptions = (isSelected) => {

        if (!isSelected) {
            return null;
        }

        if (this.state.tagPrefixBehavior === "specific") {
            return <TextField
                value={this.state.tagPrefixBehaviorValue}
                onChange={this.onTagPrefixBehaviorValueChange}
                autoComplete={false}
                clearButton={true}
                onClearButtonClick={() => {
                    this.setState({ tagPrefixBehaviorValue: "" });
                }}
                error={this.state.tagPrefixBehaviorValue == null || this.state.tagPrefixBehaviorValue === "" ? "Value is required" : null}
                placeholder={"Enter prefix to ignore. For example 'vendor_'"}
            />
        }

        if (this.state.tagPrefixBehavior === "delimiter") {
            return <Select
                options={PrefixDelimitCharacterOptions}
                onChange={this.onTagPrefixBehaviorValueChange}
                error={this.state.tagPrefixBehaviorValue == null || this.state.tagPrefixBehaviorValue === "" ? "Value is required" : null}
                value={this.state.tagPrefixBehaviorValue}
            />;
        }

        return null;
    }

    handleCapitalizationTypeChange = (newValue) => {
        this.setState({
            capitalizationType: newValue[0],
        });
    }

    onTagPrefixBehaviorValueChange = (newValue) => {
        this.setState({
            tagPrefixBehaviorValue: newValue,
        });
    }

    handleTagPrefixBehaviorChange = (selected) => {

        let tagPrefixBehaviorValue = "";

        if (selected[0] === "delimiter") {
            tagPrefixBehaviorValue = "Semicolon";
        }

        this.setState({
            tagPrefixBehavior: selected[0],
            tagPrefixBehaviorValue: tagPrefixBehaviorValue
        });
    }

    handleAutocompleteSelect = (selected) => {
        if (selected.length <= 0) {
            this.setState({
                inputValue: '',
                targetTag: null
            });
            return;
        }

        this.setState({
            inputValue: selected[0],
            targetTag: selected[0]
        });
    }

    getTags = () => {

        if (this.state.resourceType === "Product") {
            return this.state.data.tags;
        }

        if (this.state.resourceType === "Customer") {
            return this.state.data.customerTags;
        }

        if (this.state.resourceType === "Order") {
            return this.state.data.orderTags;
        }

        return [];
    }

    onResourceChange = (value) => {
        this.setState({
            resourceType: value,
        }, () => this.updateText(''));
    }

    updateText = (value) => {
        this.setState({
            inputValue: value,
            targetTag: value
        });
    }

    handleSelectedTagsChange = (newSelectedTags) => {
        this.setState({
            selectedTags: newSelectedTags
        })
    }

    fetchDashboardData = () => {
        apiFetch('api/dashboard')
            .then(response => { return response.json() })
            .then(data => {
                this.setState({
                    data: data,
                    step: "create",
                    isLoading: false,
                }, () => { this.updateText('') });
            });
    }

    handlePreview = () => {

        this.setState({
            isLoading: true
        });

        apiFetch(`api/scenariosNew/changeTagCapitalization/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                selectedTags: this.state.selectedTags,
                capitalizationType: this.state.capitalizationType,
                tagPrefixBehavior: this.state.tagPrefixBehavior,
                tagPrefixBehaviorValue: this.state.tagPrefixBehaviorValue,
                resourceType: this.state.resourceType
            })
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    isLoading: false,
                    step: "preview",
                    preview: data
                });
                window.scrollTo(0, 0);
            });
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true
        });

        apiFetch(`api/scenariosNew/changeTagCapitalization/execution`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                selectedTags: this.state.selectedTags,
                capitalizationType: this.state.capitalizationType,
                tagPrefixBehavior: this.state.tagPrefixBehavior,
                tagPrefixBehaviorValue: this.state.tagPrefixBehaviorValue,
                resourceType: this.state.resourceType
            })
        }).then(_ => {
            this.props.history.push("/operations");
        });
    }

    handleBack = async () => {
        this.setState({
            step: "create"
        })
    }

    componentDidMount() {
        this.fetchDashboardData();
    }
}

export default ScenarioFixTagCapitalizationPage;